/**
 * era-control's shapes, taken from the spec rather than retyped.
 *
 * `openapi.ts` is generated by `pnpm --filter @era/shared gen:api` from the
 * LIVE `/openapi.json`, which FastAPI in turn generates from the same Pydantic
 * models that validate real traffic. Type aliases below normally point straight
 * at that output. The explicitly documented optional webhook additions are the
 * only rolling bridge: they accept both the deployed and finalized additive
 * shapes until the next post-deploy regeneration.
 *
 * Regenerate it in the same change that starts using a new field. A
 * hand-maintained spec is a spec that is wrong.
 */
/**
 * The same `satisfies` discipline as `ARTIFACT_KINDS` above: a member removed
 * or renamed upstream fails this build, while a member ADDED upstream compiles
 * and is caught at runtime by `isKnownVideoRecordingMode` instead of crashing.
 */
export const VIDEO_RECORDING_MODES = [
    'none',
    'auto',
    'focused_guest',
    'active_speaker',
    'meeting_view',
];
export const VIDEO_SCREEN_SHARE_MODES = ['replace'];
export const VIDEO_RESOLUTIONS = ['720p'];
export const VIDEO_POLICY_TRANSITIONS = [
    'stable',
    'enabling',
    'reconfiguring',
    'disabling',
    'failed',
];
export function isKnownVideoRecordingMode(value) {
    return (value !== null &&
        value !== undefined &&
        VIDEO_RECORDING_MODES.includes(value));
}
/**
 * The audio channel's transition vocabulary, which is the SAME five values.
 *
 * Written as a shared predicate rather than an inline list in the parser
 * because the parser had four of the five: `reconfiguring` was missing, and a
 * transition the parser does not recognise makes it return null, which freezes
 * every participant's retention indicator on its last understood value while
 * it still looks authoritative. A value added upstream must widen both
 * channels together or fail loudly — never freeze one quietly.
 */
export function isKnownPolicyTransition(value) {
    return isKnownVideoPolicyTransition(value);
}
export function isKnownVideoPolicyTransition(value) {
    return (value !== null &&
        value !== undefined &&
        VIDEO_POLICY_TRANSITIONS.includes(value));
}
/**
 * `focus_identity` is REQUIRED for `focused_guest` and OPTIONAL for `auto`.
 *
 * Straight from the field's own description: "Required for focused_guest and
 * optional for auto. The recorder cannot infer who is 'the other participant'
 * from join order." In the unified contract the value lives at
 * `recording.video.focus_identity`.
 *
 * Two predicates rather than one because the two questions have different
 * answers for `auto`: it ACCEPTS an identity (and uses it as the fixed focus in
 * a 1:1) but does not REQUIRE one. A client that treated "accepts" as
 * "requires" would refuse a legal spec; one that treated "requires" as
 * "accepts" would send a 422.
 */
export function videoModeRequiresFocusIdentity(mode) {
    return mode === 'focused_guest';
}
export function videoModeAcceptsFocusIdentity(mode) {
    return mode === 'focused_guest' || mode === 'auto';
}
/**
 * The closed enums, as values.
 *
 * Written out rather than derived, because a TypeScript union has no runtime
 * form — and the whole point of having them at runtime is to be able to ask
 * "did the pipeline just grow a member we do not know?" The `satisfies` clause
 * is what keeps these honest: add a member upstream, regenerate `openapi.ts`,
 * and the compiler does NOT complain (a subset still satisfies the union) —
 * but `isKnownArtifactKind` starts returning false for it at runtime, which is
 * the visible, handled case rather than a crash. Removing or renaming a member
 * upstream DOES fail the build here, which is the direction that matters.
 */
export const ARTIFACT_KINDS = [
    'playback',
    'transcript',
    'activity',
    'metrics',
    'manifest',
    'stem',
    'analysis',
];
export const ARTIFACT_STATES = [
    'planned',
    'uploaded',
    'verified',
    'deleted',
    'failed',
];
export const ARTIFACT_TIMEBASES = [
    'session',
    'source',
    'speech_gated',
    'none',
];
export function isKnownArtifactKind(value) {
    return value !== null && value !== undefined && ARTIFACT_KINDS.includes(value);
}
export function isKnownArtifactState(value) {
    return value !== null && value !== undefined && ARTIFACT_STATES.includes(value);
}
export function isKnownTimebase(value) {
    return (value !== null && value !== undefined && ARTIFACT_TIMEBASES.includes(value));
}
/**
 * An artifact whose timestamps do NOT share the session timeline.
 *
 * `speech_gated` is the dangerous one: a stem is that speaker's speech with the
 * silence cut out, so its `duration_ms` is their speaking time and second 30 of
 * the file is nowhere near second 30 of the call. Lining one up against the
 * transcript produces confident nonsense with nothing to catch it — which is
 * why this is a function in shared code rather than a comment on a screen.
 * `source` is relative to one decoded recording, which starts when that
 * participant's track did, not when the call did.
 *
 * Use typed `AudioArtifactStatus.mapping` (`metadata.mapping` on a rolling
 * compatibility response) to place a speech-gated span back onto its source timeline.
 */
export function isSessionAligned(timebase) {
    return timebase === 'session';
}
/*
 * `SessionTranscript` — what `transcript_ready` delivers INLINE — is declared
 * once, near the top of this file with the rest of the read shapes. It is the
 * same object `GET /v1/sessions/{id}/transcript` returns, which is the point:
 * the push and the pull are one type, so a receiver and a fetcher cannot drift.
 */
/**
 * Upstream's hard limit on participants in ONE `POST /v1/rooms` call.
 *
 * Verified against the live spec on 2026-08-08:
 * `CreateRoomRequest.participants.maxItems == 8`. A ninth entry is a 422, not
 * a truncation. Late joiners past this go through `POST /v1/rooms/{id}/tokens`
 * instead, which is what this app does for everyone except the creator.
 *
 * See FINDINGS.md 2026-08-08 #2: the brief this app was built from said 32.
 */
export const MAX_PARTICIPANTS_PER_CREATE = 8;
/**
 * `participant_limit` is an enum, not a range: `[2, 4, 8]`, mapping to the
 * P2/P4/P8 capacity reservation profiles that consume 1/2/4 accepted capacity
 * units. Sending 3 is a 422.
 */
export const PARTICIPANT_LIMITS = [2, 4, 8];
/** `max_duration_minutes`: 1..600, default 180. */
export const MAX_DURATION_MINUTES_LIMIT = 600;
export const DEFAULT_DURATION_MINUTES = 180;
/**
 * A session is finished changing.
 *
 * `complete`, `partial`, `failed` and `purged` are terminal; `pending`, `live`
 * and `ended` are not. `ended` specifically is NOT terminal — the call is over
 * but the transcript pipeline is still running, which is exactly the window
 * where a naive poller stops one refresh too early and the transcript never
 * appears.
 */
export function isTerminalState(state) {
    return state === 'complete' || state === 'partial' || state === 'failed' || state === 'purged';
}
//# sourceMappingURL=era.js.map